/**
 * BarChart Component
 * Displays bar chart data with click handling for drill-down
 */

import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Box } from '@mui/material';
import type { BarChartDataDto } from '@/api/types/dtos/analytics/AnalyticsDto';
import { sortByDateLabel } from '../../utils/dateLabelSort';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface BarChartProps {
  data: BarChartDataDto;
  onClick: (value: string) => void;
}

export const BarChart: React.FC<BarChartProps> = ({ data, onClick }) => {
  // Safety check for data
  if (!data || !data.labels || !data.values || data.labels.length === 0) {
    return (
      <Box
        sx={{
          height: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-secondary)',
        }}
      >
        No data available
      </Box>
    );
  }

  // Sort data chronologically when labels are date-like (week/month/quarter/year/day);
  // categorical labels keep the alphabetical fallback inside the util.
  const sortedData = sortByDateLabel(
    data.labels.map((label, i) => ({
      label,
      value: data.values[i],
      color: data.colors?.[i],
    }))
  );

  // Convert labels with \n to multi-line arrays for Chart.js
  const formatLabels = (labels: string[]) => {
    return labels.map(label => {
      if (label.includes('\n')) {
        return label.split('\n');
      }
      return label;
    });
  };

  const chartData = {
    labels: formatLabels(sortedData.map(d => d.label)),
    datasets: [
      {
        data: sortedData.map(d => d.value),
        backgroundColor: sortedData.map(d => d.color || '#1976d2'),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 10,
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const value = sortedData[index].label;
        onClick(value);
      }
    },
    onHover: (event: any, elements: any[]) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'nearest' as const,
        intersect: false,
        // Expand hit area for easier interaction with small bars
        hitRadius: 20,
        hoverRadius: 10,
        callbacks: {
          label: (context: any) => {
            return `${context.parsed.y}`;
          },
        },
      },
    },
    datasets: {
      bar: {
        barPercentage: 0.9,
        categoryPercentage: 0.8,
        maxBarThickness: 60,
        minBarLength: 2, // Ensure minimum visible length for bars
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          precision: 0,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          // Support multi-line labels (Chart.js handles \n automatically)
          maxRotation: 0,
          autoSkip: true,
        },
      },
    },
  };

  return (
    <Box sx={{ height: 320, width: '100%', position: 'relative' }}>
      <Bar options={options} data={chartData} />
    </Box>
  );
};
